// LAYOUT-SPEC §0 — a team (wireframe 3), and the contract-wide group
//
//   ONE QUESTION  What does this team owe, how does its SOW say to carry each
//                 deliverable out, and what is due or wrong right now?
//   PRIMARY OBJECT  One workstream (Archetype A, record cockpit with a strip).
//   PRIMARY VERB    + Submit. Handling a deliverable, adding a SOW and linking
//                 an exercise line are the setup verbs.
//   WORST-CASE VOLUME  ~40 deliverables across two or three SOW versions.
//
// A team is only a grouping: its deliverables come from award rules; its SOWs
// say how each is handled here (performed, routed, not owed). The strip is the
// team's own current window (D104). /workstreams/contract-wide is the same
// shell for the dates no team owes, "Contract-wide (<period>)" (D109).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBreadcrumbs, SpecraButton, SpecraDashboardPage, SpecraDateField, SpecraEmptyState, SpecraFormField, SpecraHeading, SpecraInlineBanner, SpecraInput, SpecraLink, SpecraPageHeader, SpecraRecordGroups, SpecraSectionHeader, SpecraSelect, SpecraTag, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowRight, Link2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageBody } from '../../../../components/layout';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { ErrorState } from '../../_shared/components/PageStates';
import { useCmAccess } from '../access';
import { AwaitingApprovalBanner } from '../components/AwaitingApproval';
import { CmDrawer } from '../components/CmDrawer';
import { CmAwaitingApproval, CmSourceButton, CmStateTable, CmStatusBadge, CmTable, SowStateBadge } from '../components/common';
import { LOADING_STRIP, stripProps, useTeamKpi } from '../components/KpiStrip';
import { useCmDrawerParams } from '../drawerParams';
import { OccurrenceWindow, SowGapChoice, useCreateSow, useDutiesDetail, useOccurrences, useRuleSowGap, useSetExerciseWorkstream, useWorkstream, useWorkstreamDetails } from '../hooks';
import { cmDate, cmDateShort, cmSpan } from '../lib/format';
import { HANDLING_LABEL, isOccurrenceState, isOpenState, NOT_OWED_REASON_LABEL, occurrenceStatus, STATE_SHORT_LABEL } from '../lib/status';
import { useCmLookups } from '../lookups';
import { useContractWideLabel } from './TeamsPage';
/** The date a row points at: the earliest open one, else the latest. */ function nextOf(occs) {
    const open = occs.filter((o)=>isOpenState(o.status?.state ?? '')).sort((a, b)=>(a.dueOn || '9').localeCompare(b.dueOn || '9'));
    if (open.length) return open[0];
    return [
        ...occs
    ].sort((a, b)=>(b.dueOn || '').localeCompare(a.dueOn || ''))[0];
}
function sourceLine(d) {
    const who = d.origin === 'sow' ? 'SOW' : d.origin === 'manual' ? 'By hand ·' : 'Award';
    return `${who}${d.pwsRef ? ` PWS ${d.pwsRef}` : ''}${d.letter ? ` ${d.letter}` : ''}`;
}
export function WorkstreamPage() {
    const { wsId = '' } = useParams();
    return wsId === 'contract-wide' ? /*#__PURE__*/ _jsx(ContractWideView, {}) : /*#__PURE__*/ _jsx(TeamView, {
        wsId: wsId
    });
}
function useRowColumns(programId, teamName, withHandling, onHandling) {
    const params = useCmDrawerParams();
    const cols = [
        {
            id: 'title',
            header: 'Deliverable',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "font-medium",
                    children: r.duty.title
                })
        },
        {
            id: 'source',
            header: 'Source',
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    className: "flex items-center gap-1.5 text-muted-foreground",
                    children: [
                        sourceLine(r.duty),
                        /*#__PURE__*/ _jsx(CmSourceButton, {
                            locator: r.duty.sourceLocator
                        })
                    ]
                })
        }
    ];
    if (withHandling) cols.push({
        id: 'handling',
        header: 'Handling here',
        cell: (r)=>{
            const a = r.assignment;
            if (!a) return /*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground",
                children: "Award’s schedule"
            });
            const quote = a.overrideLocators.find((l)=>l.quote)?.quote;
            const text = a.handling === 'routed' ? `Routed to ${a.routedToWorkstreamId ? teamName(a.routedToWorkstreamId) : 'base scope'}` : a.handling === 'not_owed' ? `Not owed: ${NOT_OWED_REASON_LABEL[a.notOwedReason] ?? a.notOwedReason}` : `${HANDLING_LABEL.performed}${a.teamRules.length ? ' · team rule' : ''}`;
            return /*#__PURE__*/ _jsxs("span", {
                className: "flex items-center gap-1.5",
                "data-testid": Testids.cmWorkstream.handling,
                children: [
                    onHandling ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "link",
                        size: "inline",
                        className: "text-left",
                        title: "Change how this SOW handles it",
                        onClick: (e)=>{
                            e.stopPropagation();
                            onHandling(r);
                        },
                        children: text
                    }) : text,
                    /*#__PURE__*/ _jsx(CmSourceButton, {
                        locator: a.sourceLocator,
                        quote: quote
                    })
                ]
            });
        }
    });
    cols.push({
        id: 'next',
        header: 'Next / state',
        align: 'right',
        cell: (r)=>r.next ? /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex flex-wrap items-center justify-end gap-1",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "ghost",
                        size: "sm",
                        className: "gap-2",
                        onClick: (e)=>{
                            e.stopPropagation();
                            params.open('occ', r.next?.id ?? '');
                        },
                        "data-testid": Testids.cmWorkstream.nextState,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "tabular-nums text-muted-foreground",
                                children: cmDateShort(r.next.dueOn)
                            }),
                            /*#__PURE__*/ _jsx(CmStatusBadge, {
                                status: occurrenceStatus(r.next, r.duty.finalReviewTier)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CmAwaitingApproval, {
                        programId: programId,
                        occurrenceId: r.next.id
                    })
                ]
            }) : r.assignment?.handling === 'routed' && r.assignment.routedToWorkstreamId ? /*#__PURE__*/ _jsxs(SpecraLink, {
                href: `/delivery/programs/${programId}/workstreams/${r.assignment.routedToWorkstreamId}`,
                renderLink: renderRouterLink,
                className: "inline-flex items-center gap-1",
                onClick: (e)=>e.stopPropagation(),
                children: [
                    "Go ",
                    /*#__PURE__*/ _jsx(ArrowRight, {
                        className: "h-3 w-3",
                        "aria-hidden": true
                    })
                ]
            }) : /*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground",
                children: "No dates"
            })
    });
    return cols;
}
function StateFilterList({ programId, occs, finalTierOf }) {
    const [sp, setSp] = useSearchParams();
    const lk = useCmLookups(programId);
    const params = useCmDrawerParams();
    const state = sp.get('state') ?? '';
    if (!state) return null;
    const rows = occs.filter((o)=>(o.status?.state ?? '') === state);
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        isOccurrenceState(state) ? STATE_SHORT_LABEL[state] : state,
                        " · ",
                        rows.length
                    ]
                }),
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "ghost",
                    onClick: ()=>setSp((prev)=>{
                            const n = new URLSearchParams(prev);
                            n.delete('state');
                            return n;
                        }),
                    children: "Clear"
                })
            }),
            /*#__PURE__*/ _jsx(CmStateTable, {
                programId: programId,
                occurrences: rows,
                dutyTitle: (id)=>lk.dutyById.get(id)?.title,
                finalTierOf: finalTierOf,
                teamName: lk.teamName,
                onOpen: (id)=>params.open('occ', id),
                empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    size: "compact",
                    title: "Nothing in this state.",
                    description: "No date in this window is in it right now."
                })
            })
        ]
    });
}
function TeamView({ wsId }) {
    const { programId } = useProgramContext();
    const navigate = useNavigate();
    const access = useCmAccess(programId);
    const params = useCmDrawerParams();
    const lk = useCmLookups(programId);
    const ws = useWorkstream(programId, wsId);
    const occs = useOccurrences(programId, {
        workstreamId: wsId,
        window: OccurrenceWindow.WORKSTREAM_CURRENT_WINDOW
    });
    const [addSow, setAddSow] = useState(null);
    const [closedSows, setClosedSows] = useState([]);
    const [linking, setLinking] = useState(false);
    const details = useDutiesDetail(programId, lk.duties.map((d)=>d.id));
    const columns = useRowColumns(programId, lk.teamName, true, access.canWrite ? (r)=>r.assignment && params.open('assign', `${wsId}:${r.assignment.sowId}:${r.duty.id}`) : undefined);
    const ruleGap = useRuleSowGap(programId, {
        success: 'Ruling recorded'
    });
    const kpi = useTeamKpi(programId, wsId, {
        enabled: (ws.data?.exerciseLines ?? []).length > 0
    });
    const data = ws.data;
    const sows = useMemo(()=>[
            ...data?.sows ?? []
        ].sort((a, b)=>(b.popStart || '').localeCompare(a.popStart || '')), [
        data?.sows
    ]);
    const lines = data?.exerciseLines ?? [];
    const lineOts = new Set(lines.map((l)=>l.optionalTaskId));
    const occList = occs.data?.occurrences ?? [];
    const { bySow, unhandled } = useMemo(()=>{
        const sowIds = new Set(sows.map((s)=>s.id));
        const bySow = new Map();
        const handled = new Set();
        const occByDuty = new Map();
        for (const o of occList){
            const arr = occByDuty.get(o.dutyId) ?? [];
            arr.push(o);
            occByDuty.set(o.dutyId, arr);
        }
        lk.duties.forEach((duty, i)=>{
            const assignments = details[i]?.data?.assignments ?? [];
            for (const a of assignments){
                if (!sowIds.has(a.sowId)) continue;
                handled.add(duty.id);
                const arr = bySow.get(a.sowId) ?? [];
                arr.push({
                    duty,
                    assignment: a,
                    next: nextOf(occByDuty.get(duty.id) ?? [])
                });
                bySow.set(a.sowId, arr);
            }
        });
        const owedHere = lk.duties.filter((d)=>!handled.has(d.id) && (occByDuty.has(d.id) || (lk.rulesByDuty.get(d.id) ?? []).some((r)=>r.optionalTaskId && lineOts.has(r.optionalTaskId))));
        return {
            bySow,
            unhandled: owedHere.map((duty)=>({
                    duty,
                    next: nextOf(occByDuty.get(duty.id) ?? [])
                }))
        };
    }, [
        sows,
        lk.duties,
        lk.rulesByDuty,
        details,
        occList,
        lineOts
    ]);
    if (ws.error) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(ErrorState, {
                error: ws.error,
                title: "Could not load this team",
                onRetry: ()=>void ws.refetch()
            })
        });
    }
    const team = data?.workstream;
    // A deliverable whose detail fails to load has no SOW rows here; say which,
    // never leave it silently missing.
    const missingDetail = lk.duties.filter((_, i)=>details[i]?.isError);
    const finalTierOf = (dutyId)=>lk.dutyById.get(dutyId)?.finalReviewTier ?? 'va_pm';
    const laterSowFor = (gap)=>[
            ...sows
        ].sort((a, b)=>(a.popStart || '').localeCompare(b.popStart || '')).find((s)=>(s.popStart || '') > gap.fromOn);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
                "data-testid": Testids.cmWorkstream.page,
                metrics: lines.length > 0 ? kpi.strip : undefined,
                header: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBreadcrumbs, {
                            renderLink: renderRouterLink,
                            items: [
                                {
                                    id: 'teams',
                                    label: 'Teams',
                                    href: `/delivery/programs/${programId}/workstreams`
                                },
                                {
                                    id: 'team',
                                    label: team?.name ?? 'Team',
                                    current: true
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraPageHeader, {
                            titleSize: "home",
                            title: team?.name ?? 'Team',
                            actions: /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    access.canWrite ? /*#__PURE__*/ _jsxs(SpecraButton, {
                                        variant: "secondary",
                                        onClick: ()=>setLinking(true),
                                        "data-testid": Testids.cmWorkstream.linkExercise,
                                        children: [
                                            /*#__PURE__*/ _jsx(Link2, {
                                                className: "h-4 w-4",
                                                "aria-hidden": true
                                            }),
                                            " Link exercise"
                                        ]
                                    }) : null,
                                    access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>params.open('submit', 'new', {
                                                forTeam: wsId
                                            }),
                                        "data-testid": Testids.cmWorkstream.submit,
                                        children: "+ Submit"
                                    }) : null
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1 text-[length:var(--fs-ui-body)]",
                            "data-testid": Testids.cmWorkstream.header,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "Exercises:"
                                        }),
                                        lines.length === 0 ? /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "none linked yet"
                                        }) : lines.map((l)=>/*#__PURE__*/ _jsxs("span", {
                                                className: "inline-flex items-center gap-1.5",
                                                children: [
                                                    /*#__PURE__*/ _jsxs(SpecraTag, {
                                                        "data-testid": Testids.cmWorkstream.exerciseLink,
                                                        children: [
                                                            lk.optionalTaskById.get(l.optionalTaskId)?.short ?? 'OT',
                                                            " ",
                                                            l.clinNumber
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-muted-foreground",
                                                        children: [
                                                            "PoP ",
                                                            cmSpan(l.popStart, l.popEnd)
                                                        ]
                                                    })
                                                ]
                                            }, l.slinId || l.exerciseId))
                                    ]
                                }),
                                (data?.predecessors ?? []).length > 0 ? /*#__PURE__*/ _jsxs("div", {
                                    "data-testid": Testids.cmWorkstream.lineage,
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "Continues: "
                                        }),
                                        (data?.predecessors ?? []).map((p, i)=>/*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    i > 0 ? ' + ' : '',
                                                    /*#__PURE__*/ _jsx(SpecraLink, {
                                                        href: `/delivery/programs/${programId}/workstreams/${p.predecessorWorkstreamId}`,
                                                        renderLink: renderRouterLink,
                                                        children: lk.teamName(p.predecessorWorkstreamId)
                                                    })
                                                ]
                                            }, p.id)),
                                        data?.predecessors[0]?.effectiveOn ? /*#__PURE__*/ _jsxs("span", {
                                            className: "text-muted-foreground",
                                            children: [
                                                ' ',
                                                "— from ",
                                                cmDate(data.predecessors[0].effectiveOn)
                                            ]
                                        }) : null
                                    ]
                                }) : null
                            ]
                        })
                    ]
                }),
                children: [
                    lines.length > 0 ? kpi.windowNote : null,
                    /*#__PURE__*/ _jsx(AwaitingApprovalBanner, {
                        programId: programId,
                        team: wsId
                    }),
                    missingDetail.length > 0 ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "warning",
                        variant: "framed",
                        "data-testid": Testids.cmWorkstream.dutyDetailMissing,
                        title: `${missingDetail.length} deliverable${missingDetail.length === 1 ? '' : 's'} could not be loaded`,
                        children: [
                            "Their SOW assignments are missing from this page:",
                            ' ',
                            missingDetail.map((d)=>d.title || d.dutyKey).join(', '),
                            "."
                        ]
                    }) : null,
                    (data?.openGaps ?? []).map((gap)=>{
                        const later = laterSowFor(gap);
                        return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                            tone: "warning",
                            variant: "framed",
                            "data-testid": Testids.cmWorkstream.gapBanner,
                            action: access.canWrite ? /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap gap-2",
                                children: [
                                    later ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        "data-testid": Testids.cmWorkstream.gapChoice,
                                        "data-choice": "latest_sow",
                                        onClick: ()=>ruleGap.mutate({
                                                workstreamId: wsId,
                                                fromOn: gap.fromOn,
                                                toOn: gap.toOn,
                                                choice: SowGapChoice.LATEST_SOW,
                                                sowId: later.id
                                            }),
                                        children: "The latest SOW applied all along"
                                    }) : null,
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        "data-testid": Testids.cmWorkstream.gapChoice,
                                        "data-choice": "award",
                                        onClick: ()=>ruleGap.mutate({
                                                workstreamId: wsId,
                                                fromOn: gap.fromOn,
                                                toOn: gap.toOn,
                                                choice: SowGapChoice.AWARD
                                            }),
                                        children: "The award’s schedule applied"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        "data-testid": Testids.cmWorkstream.gapChoice,
                                        "data-choice": "upload_earlier",
                                        onClick: ()=>setAddSow({
                                                gap
                                            }),
                                        children: "Add the earlier version"
                                    })
                                ]
                            }) : undefined,
                            children: [
                                "No SOW covers ",
                                cmSpan(gap.fromOn, gap.toOn),
                                ". Until someone says which schedule applied, deliverables the later SOW changes have no dates in those months."
                            ]
                        }, `${gap.fromOn}-${gap.toOn}`);
                    }),
                    lines.length > 0 && sows.length === 0 ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "info",
                        variant: "framed",
                        "data-testid": Testids.cmWorkstream.notOnFileBanner,
                        action: access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: ()=>setAddSow({}),
                            "data-testid": Testids.cmWorkstream.addSow,
                            children: "Add SOW"
                        }) : undefined,
                        children: "No SOW on file: the award’s schedule governs this team’s deliverables."
                    }) : null,
                    lines.length === 0 && !ws.isLoading ? /*#__PURE__*/ _jsx("div", {
                        "data-testid": Testids.cmWorkstream.noExercises,
                        children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            framed: true,
                            title: "Link an exercise line to start this team’s schedule",
                            description: "An exercise line (e.g. 11001-17) authorizes a team. Until one is linked, nothing is owed here.",
                            action: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>setLinking(true),
                                children: "Link exercise"
                            }) : undefined
                        })
                    }) : null,
                    /*#__PURE__*/ _jsx(StateFilterList, {
                        programId: programId,
                        occs: occList,
                        finalTierOf: finalTierOf
                    }),
                    sows.length > 0 ? /*#__PURE__*/ _jsx(SpecraRecordGroups, {
                        className: "space-y-5 sm:space-y-6",
                        variant: "hairline",
                        openIds: sows.filter((sow)=>!closedSows.includes(sow.id)).map((sow)=>sow.id),
                        onOpenChange: (ids)=>setClosedSows(sows.filter((sow)=>!ids.includes(sow.id)).map((sow)=>sow.id)),
                        getGroupTestId: ()=>Testids.cmWorkstream.sowBlock,
                        getGroupProps: (group)=>({
                                'data-row-id': group.id
                            }),
                        groups: sows.map((sow)=>{
                            const rows = (bySow.get(sow.id) ?? []).sort((a, b)=>a.duty.title.localeCompare(b.duty.title));
                            return {
                                id: sow.id,
                                header: {
                                    title: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            "SOW",
                                            sow.version ? ` · v${sow.version}` : ''
                                        ]
                                    }),
                                    actions: access.canCreate && sow === sows[0] || access.canWrite ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            access.canCreate && sow === sows[0] ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "ghost",
                                                onClick: ()=>setAddSow({}),
                                                "data-testid": Testids.cmWorkstream.addSow,
                                                children: "Add SOW version"
                                            }) : null,
                                            access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "secondary",
                                                onClick: ()=>params.open('assign', `${wsId}:${sow.id}:new`),
                                                "data-testid": Testids.cmWorkstream.handleDuty,
                                                children: "+ Handle an award deliverable"
                                            }) : null
                                        ]
                                    }) : undefined
                                },
                                content: /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraHeading, {
                                            as: "h3",
                                            size: "sm",
                                            children: sow.title
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex flex-wrap items-center gap-2 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                            children: [
                                                /*#__PURE__*/ _jsx(SowStateBadge, {
                                                    state: sow.state
                                                }),
                                                sow.approvedOn ? /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        "Approved ",
                                                        cmDate(sow.approvedOn)
                                                    ]
                                                }) : null,
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: cmSpan(sow.popStart, sow.popEnd)
                                                }),
                                                sow.state === 'approved_waiting' ? /*#__PURE__*/ _jsx("span", {
                                                    children: "Nothing is scheduled until an exercise line covers these dates."
                                                }) : null
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(CmTable, {
                                            caption: `Deliverables under ${sow.title}`,
                                            rows: rows,
                                            columns: columns,
                                            rowKey: (r)=>r.duty.id,
                                            rowTestId: Testids.cmWorkstream.dutyRow,
                                            onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.duty.id}`),
                                            getRowLabel: (r)=>r.duty.title,
                                            empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                                size: "compact",
                                                title: "No deliverables handled here yet.",
                                                description: "This SOW says nothing about an award deliverable yet.",
                                                action: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "inline",
                                                    variant: "link",
                                                    onClick: ()=>params.open('assign', `${wsId}:${sow.id}:new`),
                                                    children: "Handle an award deliverable"
                                                }) : undefined
                                            })
                                        })
                                    ]
                                })
                            };
                        })
                    }) : null,
                    unhandled.length > 0 ? /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        "data-testid": Testids.cmWorkstream.unhandledSection,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: sows.length > 0 ? `Award deliverables with no handling recorded here · ${unhandled.length}` : `Owed under the award’s schedule · ${unhandled.length}`
                            }),
                            /*#__PURE__*/ _jsx(CmTable, {
                                caption: "Award deliverables owed by this team",
                                rows: unhandled,
                                columns: columns,
                                rowKey: (r)=>r.duty.id,
                                rowTestId: Testids.cmWorkstream.dutyRow,
                                onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.duty.id}`),
                                getRowLabel: (r)=>r.duty.title
                            })
                        ]
                    }) : null
                ]
            }),
            addSow ? /*#__PURE__*/ _jsx(AddSowDrawer, {
                programId: programId,
                workstreamId: wsId,
                gap: addSow.gap,
                onClose: ()=>setAddSow(null)
            }) : null,
            linking ? /*#__PURE__*/ _jsx(LinkExerciseDrawer, {
                programId: programId,
                workstreamId: wsId,
                onClose: ()=>setLinking(false)
            }) : null
        ]
    });
}
function ContractWideView() {
    const { programId } = useProgramContext();
    const navigate = useNavigate();
    const lk = useCmLookups(programId);
    const label = useContractWideLabel(programId);
    const occs = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD
    });
    const columns = useRowColumns(programId, lk.teamName, false);
    const wide = (occs.data?.occurrences ?? []).filter((o)=>!o.workstreamId && !o.retired);
    const rows = useMemo(()=>{
        const byDuty = new Map();
        for (const o of wide){
            const arr = byDuty.get(o.dutyId) ?? [];
            arr.push(o);
            byDuty.set(o.dutyId, arr);
        }
        return [
            ...byDuty.entries()
        ].map(([dutyId, list])=>({
                dutyId,
                list,
                duty: lk.dutyById.get(dutyId)
            })).filter((x)=>!!x.duty).map(({ duty, list })=>({
                duty,
                next: nextOf(list)
            })).sort((a, b)=>a.duty.title.localeCompare(b.duty.title));
    }, [
        wide,
        lk.dutyById
    ]);
    const counts = {};
    for (const o of wide)counts[o.status?.state ?? ''] = (counts[o.status?.state ?? ''] ?? 0) + 1;
    const window = occs.data?.window;
    return /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
        "data-testid": Testids.cmWorkstream.page,
        header: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraBreadcrumbs, {
                    renderLink: renderRouterLink,
                    items: [
                        {
                            id: 'teams',
                            label: 'Teams',
                            href: `/delivery/programs/${programId}/workstreams`
                        },
                        {
                            id: 'team',
                            label,
                            current: true
                        }
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    titleSize: "home",
                    title: label,
                    description: window && window.basis !== 'none' ? `Deliverables the contract owes that no team does · ${cmSpan(window.startOn, window.endOn)}` : 'Deliverables the contract owes that no team does'
                })
            ]
        }),
        metrics: occs.isLoading ? LOADING_STRIP : stripProps({
            counts,
            states: [
                'late',
                'awaiting_review',
                'rejected',
                'due_soon'
            ],
            hrefFor: (state)=>`/delivery/programs/${programId}/workstreams/contract-wide?state=${state}`,
            testId: Testids.cmWorkstream.strip,
            tileTestId: Testids.cmWorkstream.stripTile
        }),
        children: [
            /*#__PURE__*/ _jsx(StateFilterList, {
                programId: programId,
                occs: wide,
                finalTierOf: (id)=>lk.dutyById.get(id)?.finalReviewTier ?? 'va_pm'
            }),
            /*#__PURE__*/ _jsx(CmTable, {
                caption: "Contract-wide deliverables",
                rows: rows,
                columns: columns,
                rowKey: (r)=>r.duty.id,
                rowTestId: Testids.cmWorkstream.dutyRow,
                isLoading: occs.isLoading,
                error: occs.error,
                onRetry: ()=>void occs.refetch(),
                onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.duty.id}`),
                getRowLabel: (r)=>r.duty.title,
                empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    framed: true,
                    title: "Nothing contract-wide this period",
                    description: "Every date this period belongs to a team. Contract-wide deliverables (base-period reports, for example) appear here."
                })
            })
        ]
    });
}
function AddSowDrawer({ programId, workstreamId, gap, onClose }) {
    const create = useCreateSow(programId, {
        success: 'SOW added',
        errors: 'inline'
    });
    const [title, setTitle] = useState('');
    const [version, setVersion] = useState('');
    const [approvedOn, setApprovedOn] = useState('');
    const [popStart, setPopStart] = useState(gap?.fromOn ?? '');
    const [popEnd, setPopEnd] = useState(gap?.toOn ?? '');
    const ready = title.trim() !== '' && !create.isPending;
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: gap ? 'Add the earlier SOW version' : 'Add SOW',
        description: gap ? `It should cover ${cmSpan(gap.fromOn, gap.toOn)}, the months no SOW on file covers.` : 'The SOW (or EP) this team works to. From its PoP start its terms apply.',
        onClose: onClose,
        busy: create.isPending,
        testId: Testids.cmWorkstream.sowDrawer,
        footerProps: {
            confirmLabel: 'Add SOW',
            onConfirm: ()=>create.mutate({
                    workstreamId,
                    title: title.trim(),
                    version: version.trim(),
                    approvedOn,
                    popStart,
                    popEnd
                }, {
                    onSuccess: onClose
                }),
            confirmDisabled: !ready,
            loading: create.isPending,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmWorkstream.sowSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Title",
                    required: true,
                    hint: "As the document titles itself.",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: title,
                        onChange: (e)=>setTitle(e.target.value),
                        "data-testid": Testids.cmWorkstream.sowTitle
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Version",
                    hint: "e.g. 1.4",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: version,
                        onChange: (e)=>setVersion(e.target.value),
                        "data-testid": Testids.cmWorkstream.sowVersion
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "Approved on",
                    value: approvedOn,
                    onValueChange: setApprovedOn,
                    "data-testid": Testids.cmWorkstream.sowApprovedOn
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            picker: "native",
                            label: "PoP start",
                            value: popStart,
                            onValueChange: setPopStart,
                            "data-testid": Testids.cmWorkstream.sowPopStart
                        }),
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            picker: "native",
                            label: "PoP end",
                            value: popEnd,
                            onValueChange: setPopEnd,
                            "data-testid": Testids.cmWorkstream.sowPopEnd
                        })
                    ]
                }),
                create.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(create.error, 'Could not save')
                }) : null
            ]
        })
    });
}
function LinkExerciseDrawer({ programId, workstreamId, onClose }) {
    const lk = useCmLookups(programId);
    const all = useWorkstreamDetails(programId, lk.workstreams.map((w)=>w.id));
    const link = useSetExerciseWorkstream(programId, {
        errors: 'inline',
        success: (r)=>`Exercise linked. ${r.result?.mint?.inserted ? `${r.result.mint.inserted} dates scheduled.` : 'No dates changed.'}`
    });
    const [exerciseId, setExerciseId] = useState('');
    const exercise = lk.exerciseById.get(exerciseId);
    // The line's whole reviewer set: keep every other team this line already
    // names, so linking here never unlinks someone else.
    const others = new Set();
    for (const q of all){
        for (const line of q.data?.exerciseLines ?? []){
            if (line.exerciseId !== exerciseId) continue;
            for (const w of line.workstreams)if (w.basis === 'reviewer') others.add(w.workstreamId);
        }
    }
    others.add(workstreamId);
    // Until every team's lines are read, the set above could miss one, and
    // sending it would unlink that team: Link waits for all of them.
    const allLoaded = !lk.isLoading && all.every((q)=>q.isSuccess);
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Link an exercise line",
        description: "The exercise line authorizes this team: its dates are scheduled from the line’s PoP.",
        onClose: onClose,
        busy: link.isPending,
        testId: Testids.cmWorkstream.linkExerciseDrawer,
        footerProps: {
            confirmLabel: 'Link',
            onConfirm: ()=>link.mutate({
                    exerciseId,
                    workstreamIds: [
                        ...others
                    ]
                }, {
                    onSuccess: onClose
                }),
            confirmDisabled: !exerciseId || !allLoaded || link.isPending,
            loading: link.isPending,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmWorkstream.linkExerciseSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                lk.exercises.length === 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: [
                        "No exercises recorded yet. Record one on Optional Tasks first.",
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraLink, {
                            href: `/delivery/programs/${programId}/optional-tasks`,
                            renderLink: renderRouterLink,
                            children: "Open Optional Tasks"
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Exercise",
                    required: true,
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: exerciseId,
                        onChange: (e)=>setExerciseId(e.target.value),
                        "data-testid": Testids.cmWorkstream.linkExercisePicker,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Pick the exercise…"
                            }),
                            lk.exercises.map((e)=>/*#__PURE__*/ _jsxs("option", {
                                    value: e.id,
                                    children: [
                                        lk.optionalTaskById.get(e.optionalTaskId)?.short ?? 'OT',
                                        ' ',
                                        e.clinNumber || 'exercise',
                                        " · ",
                                        cmSpan(e.start, e.end)
                                    ]
                                }, e.id))
                        ]
                    })
                }),
                exercise && !exercise.clinNumber ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "warning",
                    children: "This exercise has no line number; the link uses the exercise’s own CLIN."
                }) : null,
                link.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(link.error, 'Could not save')
                }) : null
            ]
        })
    });
}
